import { Suspense, useRef, createRef, lazy } from "react";
import { useAppHeight } from "@/shared/hooks/useAppHeight";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import routes from "./routes";
import { resolveRouteMotion, findRouteTransition } from "./transitions";
import { resolveAccess } from "./types";
import AuthGuard from "./AuthGuard";
import { DeepLinkCapture } from "./DeepLinkCapture";
import StoreGateBoundary from "./StoreGateBoundary";
import RouteLogger from "./RouteLogger";
import RouteErrorBoundary from "./RouteErrorBoundary";
import { hasToken } from "@/shared/auth/tokenStorage";
import { appType } from "@/shared/lib/appType";
import {
  useAuthSessionRecovery,
  Credit4uPasswordExpiryRecovery,
  Credit4uDesyncCsRecovery,
  Credit4uOutOfSyncRecovery,
} from "@/features/auth";
import LoadingBox from "@/shared/ui/loading/LoadingBox";
import AlertDialogHost from "@/shared/ui/AlertDialogHost";
import Toast from "@/shared/ui/Toast";
import "@/styles/transitions/page-transition.scss";
import { ROUTES } from "@/shared/config/routes";

// CSS `--page-transition-duration`(global.scss) 와 정확히 일치시켜야 한다 — 값이 어긋나면
// RTG 가 exit 노드를 CSS 애니메이션 종료 전에 언마운트해 마지막 프레임이 조기 클립된다.
const TRANSITION_TIMEOUT = 340;

// 탭 라우트 경로 집합 — 이 경로들 간 이동에서는 page-transition 을 발동하지 않는다.
const TAB_PATHS = new Set<string>([
  ROUTES.MAIN,
  ROUTES.FEES,
  ROUTES.COVERAGE_ANALYSIS,
  ROUTES.MORE,
]);

const isTabRoute = (pathname: string) => TAB_PATHS.has(pathname);

// Router 레벨에서 단일 lazy 로 관리한다.
// 탭 경로일 때 CSSTransition 내부에서 TabShell 을 직접 렌더해 인스턴스를 보존한다.
const TabShell = lazy(() => import("./TabShell"));

/**
 * 진입 라우트 `/` — 화면이 아니라 라우팅 규칙이다.
 * 스플래시는 네이티브가 소유하므로(FE 의 webReady 통지로 dismiss) FE 는 토큰 유무만 보고 즉시 넘긴다.
 *
 * AuthGuard 를 태우지 않는 것은 의도다 — 가드의 통과 분기는 WebReadySentinel 을 함께 commit 하므로,
 * 리다이렉트 전용 화면이 가드를 통과하면 목적지의 lazy subtree 가 그려지기 전에 native 통지가 나가
 * 스플래시가 조기 해제된다(사용자가 로딩 스피너를 보게 됨). routes.tsx 의 access 단일 소스에서
 * 벗어나는 유일한 예외이며, 그래서 lazy 로도 감싸지 않는다(chunk 왕복만큼 리다이렉트가 늦어진다).
 */
const RootRedirect = () => (
  <Navigate to={hasToken() ? ROUTES.MAIN : ROUTES.LOGIN} replace />
);

const TempRedirectPage = lazy(() => import("@/pages/temp/TempRedirectPage"));

/**
 * `/temp` — 앱 밖 브라우저는 외부 홈페이지로 보내고, 앱 WebView 안에서는 나가지 않고 진입 규칙을 따른다 (#412).
 *
 * Android App Link 필터는 path 제한 없이 host 전체를 받아 QR 의 `/temp` 도 앱이 열고 WebView 에 그대로
 * 로드한다 — 앱 여부를 가르지 않으면 앱 안에 외부 페이지가 뜬다. 앱 판별은 스토어 게이트와 같은 UA 토큰.
 *
 * `/` 와 같은 이유로 AuthGuard 를 태우지 않는다 — 앱 분기는 리다이렉트 전용이라, 가드를 통과하면
 * 목적지가 그려지기 전에 webReady 가 나가 콜드 스타트 스플래시가 조기 해제된다(RootRedirect 주석 참조).
 */
const TempEntry = () => (appType() === "Web" ? <TempRedirectPage /> : <RootRedirect />);

const PageTransitionWrapper = () => {
  useAppHeight();
  const location = useLocation();
  const navType = useNavigationType();
  const nodeRefs = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(new Map());

  const getNodeRef = (key: string) => {
    if (!nodeRefs.current.has(key)) {
      nodeRefs.current.set(key, createRef<HTMLDivElement>());
    }
    return nodeRefs.current.get(key)!;
  };

  // 탭 그룹 내 이동에서는 "main-tabs" 로 key 를 고정해 page-transition enter/exit 가 발동하지 않게 한다.
  // 비-탭 ↔ 탭 전환에서는 key 가 바뀌므로 기존 exit/enter 애니메이션이 발동된다.
  // CSSTransition key 와 nodeRef 키가 동일해야 react-transition-group 이 올바르게 동작한다.
  const transitionKey = isTabRoute(location.pathname) ? "main-tabs" : location.pathname;

  const isTab = isTabRoute(location.pathname);

  // 전환 모션(카드 push/pop)을 렌더 중 직접 파생한다 — 별도 state 없이 매 렌더 계산하므로
  // 항상 "현재" nav-type 을 반영한다(state 래그·stale 없음). mode/dir 는 아래
  // .route-transition-root 의 data 속성으로 CSS 캐스케이드에 노출되고, CSSTransition 은 항상
  // classNames="route" 로 고정한다 — 방향/모드는 data 로만 결정되므로, 나가는 카드가 이전
  // 렌더의 classNames(stale)를 들고 나가 잘못된 전환(예: 탭 전환 직후 fade-exit)이 걸리지 않는다.
  // 탭 라우트는 main-tabs 고정 key 라 CSSTransition 이 재생되지 않으므로 override 조회는 건너뛴다.
  const override = isTab ? undefined : findRouteTransition(location.pathname);
  const { mode, dir } = resolveRouteMotion(navType, override);
  // "none" 은 매칭 CSS 규칙이 없어 무전환이고, timeout 0 으로 exit 노드를 즉시 언마운트한다.
  const timeout = mode === "none" ? 0 : TRANSITION_TIMEOUT;

  // react-transition-group 요구사항: 경로별로 안정적인 nodeRef 가 필요하다.
  // ref 객체의 신원만 사용할 뿐 .current 값을 렌더링 분기에 쓰지 않으므로 안전한 패턴이다.
  // eslint-disable-next-line react-hooks/refs
  const nodeRef = getNodeRef(transitionKey);

  // 탭 라우트 access 정책은 routes 배열에서 MAIN 라우트 설정을 참조한다.
  const tabRouteConfig = routes.find((r) => r.path === ROUTES.MAIN);
  const tabAccess = tabRouteConfig ? resolveAccess(tabRouteConfig) : "authenticated";

  return (
    // data-mode/data-nav: 카드 push/pop 방향·모드를 CSS 캐스케이드로 전달(display:contents 라
    // 레이아웃 무영향). 나가는 카드도 애니메이션 시점의 "현재" 값을 읽는다.
    <div className="route-transition-root" data-mode={mode} data-nav={dir}>
      <TransitionGroup component={null}>
        <CSSTransition
          key={transitionKey}
          nodeRef={nodeRef}
          timeout={timeout}
          classNames="route"
          appear
          unmountOnExit
          onExited={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <div ref={nodeRef} className="route-wrapper">
            <Suspense fallback={<LoadingBox />}>
              {isTab ? (
                <AuthGuard access={tabAccess}>
                  <TabShell />
                </AuthGuard>
              ) : (
                // 비-탭 라우트: 기존 Routes 매칭. 탭 경로는 필터링해 Routes 에서 제외한다.
                <Routes location={location}>
                  {/* `/` 는 라우팅 규칙이라 AuthGuard 를 태우지 않는다 (RootRedirect 주석 참조). */}
                  <Route path={ROUTES.ROOT} element={<RootRedirect />} />
                  <Route path={ROUTES.TEMP} element={<TempEntry />} />
                  {routes
                    .filter((r) => !TAB_PATHS.has(r.path))
                    .map((route) => (
                      <Route
                        key={route.path}
                        path={route.path}
                        element={
                          <AuthGuard access={resolveAccess(route)}>{route.element}</AuthGuard>
                        }
                      />
                    ))}
                </Routes>
              )}
            </Suspense>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
};

// 세션 무효 이벤트 구독은 BrowserRouter 내부에서 마운트해야 navigate 가 동작한다.
const AuthSessionRecovery = () => {
  useAuthSessionRecovery();
  return null;
};

const Router = () => {
  return (
    <BrowserRouter>
      <AuthSessionRecovery />
      {/* 알림톡 등 딥링크(?deeplink=) 전역 캡처 — AuthGuard 밖(리다이렉트 이전에 의도 저장). */}
      <DeepLinkCapture />
      {/* 개발기(dev·stg) 라우트 이동 콘솔 로거 — logcat 디버깅 보조, prd 제외. */}
      <RouteLogger />
      {/* 신용정보원 비밀번호 만료 전역 팝업 — 버스 구독 + navigate 위해 BrowserRouter 내부 */}
      <Credit4uPasswordExpiryRecovery />
      <Credit4uDesyncCsRecovery />
      <Credit4uOutOfSyncRecovery />
      {/* 청크 로드 실패(배포 세대 불일치) 복구 경계(#198) — 라우트 subtree 만 감싸 형제
          호스트(LoadingBox/AlertDialogHost/Toast)를 렌더 에러로부터 보존한다. */}
      <RouteErrorBoundary>
        {/* prd 브라우저 게이트 2차(#401) — 예외 경로 부팅 후 내부 이동을 막는다. PageTransitionWrapper
            (RootRedirect·TabShell·AuthGuard·TransitionGroup) 바깥이어야 게이트 시 아무것도 마운트되지 않는다. */}
        <StoreGateBoundary>
          <PageTransitionWrapper />
        </StoreGateBoundary>
      </RouteErrorBoundary>
      <LoadingBox />
      <AlertDialogHost />
      <Toast />
    </BrowserRouter>
  );
};

export default Router;
